import type { CSSProperties, ReactNode } from 'react';
import { useState } from 'react';
import { Checkbox } from './Checkbox.js';

/** La barra cian a la izquierda de una fila que espera algo tuyo.
 *
 *  Se exporta porque cuatro listas —Prospectos, Proyectos, Tareas y las
 *  respuestas de un formulario— dibujan sus filas a mano en vez de usar
 *  `ListItem`, y la marca tiene que ser la MISMA en las ocho. Copiada a mano en
 *  cada una, la primera que cambie deja las otras siete distintas. */
export const BARRA_PENDIENTE = 'inset var(--evo-list-row-selected-bar) 0 0 0 var(--evo-cyan)';

export interface ListItemProps {
  selected?: boolean;
  onSelect?: (selected: boolean) => void;
  title: string;
  subtitle?: string;
  /** @deprecated V2: el subtítulo va SIEMPRE a una línea con elipsis —
   * dos líneas rompen los 72px de fila (regla dura del handoff). */
  subtitleLines?: 1 | 2;
  meta?: ReactNode;
  badges?: ReactNode[];
  unread?: boolean;
  active?: boolean;
  onClick?: () => void;
  actions?: ReactNode;
  alwaysShowActions?: boolean;
  density?: 'compact' | 'default' | 'comfortable';
  itemId?: string | number;
  /** Ancla del recorrido guiado (Spotlight Pattern). Identidad, no estilo: la
   *  REGLA #16 prohíbe className y style, no un punto de anclaje. Va acá y no
   *  en un div envolvente porque un wrapper rompería el layout de la fila. */
  tourId?: string | undefined;
}

type DensityPad = { v: string; h: string };
const PADS: Record<NonNullable<ListItemProps['density']>, DensityPad> = {
  compact:     { v: '8px',  h: '10px' },
  default:     { v: 'var(--evo-list-row-py)', h: 'var(--evo-list-pad-x)' },
  comfortable: { v: 'var(--evo-list-row-py)', h: '24px' },
};

export function ListItem({
  selected,
  onSelect,
  title,
  subtitle,
  subtitleLines = 1,
  meta,
  badges,
  unread,
  active,
  onClick,
  actions,
  alwaysShowActions = false,
  density = 'default',
  itemId,
  tourId,
}: ListItemProps) {
  const [hovered, setHovered] = useState(false);
  const { v, h } = PADS[density];

  const filteredBadges = badges?.filter(Boolean) ?? [];
  const hasBadges = filteredBadges.length > 0;
  const hasMeta = meta != null;
  const showHoverActions = !alwaysShowActions && !!actions && hovered;
  const hasRightCol = hasMeta || showHoverActions || hasBadges;

  return (
    <div
      className="evo-list-item"
      data-list-item-id={itemId}
      data-tour-id={tourId}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ display: 'flex', flexDirection: 'column', cursor: onClick ? 'pointer' : 'default' }}
    >
      {/* Content row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          minHeight: 'var(--evo-list-row-min-height)',
          // La barra cian a la izquierda marca DOS cosas, y por eso comparten
          // el mismo trazo: la fila abierta (`active`) y la que todavía no
          // atendiste (`unread`). No se pisan — `active` además cambia el
          // fondo, así que la seleccionada se distingue igual.
          //
          // `unread` deja de ser "correo sin leer" y pasa a ser "esto espera
          // algo tuyo", que es lo que ya significaba: el indicador del menú
          // cuenta exactamente estas filas, y la marca dura hasta que actúes
          // sobre ellas, no hasta que pases por la pantalla.
          boxShadow: (active ?? false) || (unread ?? false) ? BARRA_PENDIENTE : 'none',
          background: active
            ? 'var(--evo-list-row-selected-bg)'
            : hovered
            ? 'var(--evo-list-row-hover-bg)'
            : 'transparent',
          transition: 'background 80ms',
        }}
      >
        {/* Checkbox zone */}
        {onSelect && (
          <div
            onClick={e => e.stopPropagation()}
            style={{
              alignSelf: 'flex-start',
              display: 'flex',
              /* El cotejo se alinea a la LÍNEA DEL TÍTULO, no al centro de la
                 fila: nudge tokenizado que se recalcula si cambia el título. */
              padding: `calc(${v} + var(--evo-list-check-nudge)) var(--evo-list-checkbox-gap) ${v} ${h}`,
              flexShrink: 0,
            }}
          >
            <Checkbox checked={!!selected} onChange={onSelect} />
          </div>
        )}

        {/* Content area — left inset comes from the checkbox zone's own
            padding when onSelect is present (default h/left is 0 here to
            avoid doubling it); when there's no checkbox (e.g. a non-bulk-
            selectable list like Billing's invoices view), this provides the
            same var(--evo-list-pad-x) inset directly, matching the always-
            present separator below and every selectable row. */}
        <div style={{ flex: 1, minWidth: 0, padding: `${v} 0 ${v} ${onSelect ? '0' : h}`, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span
              title={title}
              style={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                fontSize: 'var(--evo-list-title-fs)',
                fontWeight: unread ? 600 : 400,
                color: active ? 'var(--evo-cyan)' : 'var(--evo-fg-1)',
                fontFamily: 'var(--evo-font)',
              }}
            >
              {title}
            </span>
            {unread && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--evo-cyan)', flexShrink: 0 }} />}
          </div>

          {subtitle != null && (
            <span
              style={{
                display: 'block',
                whiteSpace: 'nowrap',
                marginTop: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: 'var(--evo-list-subtitle-fs)',
                color: 'var(--evo-fg-3)',
                fontFamily: 'var(--evo-font)',
              } as CSSProperties}
            >
              {subtitle}
            </span>
          )}

          {/* Inline actions — contact-style alwaysShowActions */}
          {alwaysShowActions && actions && (
            <div
              onClick={e => e.stopPropagation()}
              style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}
            >
              {actions}
            </div>
          )}
        </div>

        {/* Right column: meta (top) + badges (bottom) */}
        {hasRightCol && (
          <div
            style={{
              flexShrink: 0,
              minWidth: 'var(--evo-meta-w-sm)',
              maxWidth: 'var(--evo-select-w-sm)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              padding: `${v} ${h} ${v} 4px`,
            }}
          >
            {hasMeta && (
              <span style={{ fontSize: 'var(--evo-list-meta-fs)', color: 'var(--evo-fg-3)', whiteSpace: 'nowrap' }}>
                {meta}
              </span>
            )}
            {showHoverActions && (
              <div onClick={e => e.stopPropagation()} style={{ marginTop: 'auto' }}>
                {actions}
              </div>
            )}
            {hasBadges && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'nowrap',
                  gap: 4,
                  justifyContent: 'flex-end',
                  marginTop: 'auto',
                  paddingTop: 'var(--evo-list-meta-gap)',
                }}
              >
                {filteredBadges}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Inset separator */}
      <div style={{ margin: '0 var(--evo-list-pad-x)', height: '1px', background: 'var(--evo-stroke)', flexShrink: 0 }} />
    </div>
  );
}
